import { useEffect, useState } from 'react';
import type { SyncedAskNextReason, SyncedPrinterGroup } from '@/types/api';
import { apiClient } from '@/services/api';

const ASK_NEXT_TYPES = ['SAME', 'NEXT', 'PREVIOUS', 'LAST', 'FIRST', 'ENFORCED'];

interface AskNextReasonFormProps {
  formData: Partial<SyncedAskNextReason>;
  onChange: (data: Partial<SyncedAskNextReason>) => void;
  restaurantId: number;
}

export function AskNextReasonForm({ formData, onChange, restaurantId }: AskNextReasonFormProps) {
  const [printerGroups, setPrinterGroups] = useState<SyncedPrinterGroup[]>([]);

  useEffect(() => {
    if (restaurantId) {
      apiClient.getSyncedPrinterGroups(restaurantId).then(setPrinterGroups).catch(() => {});
    }
  }, [restaurantId]);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input type="text" value={formData.name || ''} onChange={(e) => onChange({ ...formData, name: e.target.value })} className="input w-full" required />
      </div>
      <div className="flex items-center">
        <input type="checkbox" id="anr-isEnabled" checked={formData.isEnabled ?? true} onChange={(e) => onChange({ ...formData, isEnabled: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="anr-isEnabled" className="ml-2 text-sm text-gray-700">Enabled</label>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
          <input type="number" value={formData.sortOrder ?? ''} onChange={(e) => onChange({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} className="input w-full" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Color Order</label>
          <input type="number" value={formData.colorOrder ?? ''} onChange={(e) => onChange({ ...formData, colorOrder: e.target.value ? parseInt(e.target.value) : undefined })} className="input w-full" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select
          value={formData.type ?? ''}
          onChange={(e) => onChange({ ...formData, type: e.target.value || undefined })}
          className="input w-full"
        >
          <option value="">-- Select --</option>
          {ASK_NEXT_TYPES.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Printer Group</label>
        <select
          value={formData.printerGroupId != null ? String(formData.printerGroupId) : ''}
          onChange={(e) => {
            const posId = e.target.value ? parseInt(e.target.value) : undefined;
            const pg = printerGroups.find((g) => g.posPrinterGroupId === posId);
            onChange({ ...formData, printerGroupId: posId, printerGroupName: pg?.name || undefined });
          }}
          className="input w-full"
        >
          <option value="">-- None --</option>
          {printerGroups.filter((pg) => pg.posPrinterGroupId != null).map((pg) => (
            <option key={pg.posPrinterGroupId} value={pg.posPrinterGroupId!}>{pg.name}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Button Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#ffffff'}
              onChange={(e) => onChange({ ...formData, buttonColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.buttonColorCode != null ? '#' + (formData.buttonColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, buttonColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#ffffff"
              className="input w-full"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : '#000000'}
              onChange={(e) => onChange({ ...formData, textColorCode: parseInt(e.target.value.slice(1), 16) })}
              className="h-9 w-14 cursor-pointer rounded border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={formData.textColorCode != null ? '#' + (formData.textColorCode >>> 0).toString(16).padStart(6, '0').slice(-6) : ''}
              onChange={(e) => {
                const hex = e.target.value.replace('#', '');
                if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                  onChange({ ...formData, textColorCode: hex.length === 6 ? parseInt(hex, 16) : undefined });
                }
              }}
              placeholder="#000000"
              className="input w-full"
            />
          </div>
        </div>
      </div>
      {formData.buttonColorCode != null && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
          <div
            className="rounded px-4 py-2 text-center font-medium"
            style={{
              backgroundColor: '#' + ((formData.buttonColorCode ?? 0xffffff) >>> 0).toString(16).padStart(6, '0').slice(-6),
              color: '#' + ((formData.textColorCode ?? 0) >>> 0).toString(16).padStart(6, '0').slice(-6),
            }}
          >
            {formData.name || 'Ask Next Reason'}
          </div>
        </div>
      )}
    </div>
  );
}
